import { useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { useMediaQuery, useTheme } from '@material-ui/core';
import { useSelector } from '@store';
import {
  useFetchMyRfqQuotationsQuery,
  useFetchQuotationUserRfqDetailQuery,
  useFetchWithdrawnQuotationPartnerQuery
} from '@generated/graphql';
import useDocuments from '@hooks/useDocuments';
import ReceivedQuotationFormPresenter from './ReceivedQuotationFormPresenter';

interface Props {
  rfqId?: string;
  partnerId?: string;
  chatRoomId?: string;
}

const ReceivedQuotationFormContainer = ({
  rfqId,
  partnerId,
  chatRoomId
}: Props) => {
  const history = useHistory();
  const matches = useMediaQuery(useTheme().breakpoints.down('md'));
  const hasPurchaseOrder = useSelector((state) => state.chat.hasPurchaseOrder);
  const selectedQuotationId = useSelector(
    (state) => state.documents.selectedQuotationId
  );
  const { onInitDocumentsCurrentId, onInitDocuments } = useDocuments();

  const { data: rfqData } = useFetchQuotationUserRfqDetailQuery({
    variables: {
      rfqId: rfqId ?? ''
    },
    fetchPolicy: 'cache-first',
    skip: !rfqId
  });

  const { data: quotationData, loading } = useFetchMyRfqQuotationsQuery({
    variables: {
      input: {
        rfqId: rfqId ?? '',
        quotationPartnerId: partnerId ?? ''
      }
    },
    skip: !rfqId || !partnerId,
    fetchPolicy: 'network-only'
  });

  const { data: partnerData } = useFetchWithdrawnQuotationPartnerQuery({
    variables: {
      quotationPartnerId: partnerId ?? ''
    },
    skip: !partnerId,
    fetchPolicy: 'cache-first'
  });

  const quotations = quotationData?.fetchMyRfqQuotations.data ?? [];
  const rfqDetail = rfqData?.fetchQuotationUserRfqDetail.data ?? undefined;
  const quotation = selectedQuotationId
    ? quotations?.find((v) => v.id === selectedQuotationId) ?? undefined
    : quotations?.[0] ?? undefined;
  const chatRoom =
    rfqDetail?.chatRoom?.find((room) => room.chatRoomId === chatRoomId) ??
    undefined;
  const isFirstCreatedQuotation =
    quotationData?.fetchMyRfqQuotations.data?.length === 1 && !hasPurchaseOrder;
  const isLatestQuotation = quotation?.latest ?? false;
  const partner = partnerData?.fetchWithdrawnQuotationPartner.data ?? undefined;

  useEffect(() => {
    return () => {
      const isQuotationView = !history.location.search.includes(
        'create_purchaseorder'
      );

      if (isQuotationView) {
        onInitDocumentsCurrentId();
      }
    };
  }, [history.location.search, onInitDocumentsCurrentId]);

  useEffect(
    // Clean up
    () => () => {
      onInitDocuments();
    },
    [onInitDocuments]
  );

  const isLoading = !quotation && loading;

  return (
    <ReceivedQuotationFormPresenter
      isLoading={isLoading}
      isFirstCreatedQuotation={isFirstCreatedQuotation}
      isLatestQuotation={isLatestQuotation}
      matches={matches}
      rfqDetail={rfqDetail}
      partner={partner}
      quotation={quotation}
      chatRoom={chatRoom}
    />
  );
};

export default ReceivedQuotationFormContainer;
